import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { billingApi, type EcPlanWithResources } from './api';
import { cn } from '@/lib/cn';
// PRD §13.4 — DPA clickthrough must precede any paid plan activation.
import { LegalAcceptanceModal } from '@/features/legal/LegalAcceptanceModal';
import { DPA } from '@/features/legal/documents';
import { legalApi } from '@/features/legal/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { toast } from '@/components/ui/Toast';
import { useAuth } from '@/lib/auth/useAuth';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import type { EcPlan } from '@/lib/api/types';

const XAMPLIN_PURPLE = 'var(--color-xk-accent, #B262F5)';

// Eventer+ is the Most-Popular tier (Xamplin pricing v1.0, decision DN-3).
const POPULAR_SLUG = 'eventer-plus';

/** Read a per-resource cap (events / agencies) from the hydrated catalog row. */
function capOf(plan: EcPlanWithResources, resource: string): number {
  return plan.resources?.find((r) => r.resource === resource)?.limit ?? 0;
}
/** "Ilimitado" for the sentinel cap, else the formatted number. */
function capLabel(n: number): string {
  return n >= 9999 ? 'Ilimitado' : n.toLocaleString();
}

// Builds the extra feature bullets from per-plan flags — methodology is the paid
// upgrade (CS-1 / MENT / ATLEDI recap), operations stay free across tiers.
function featureBullets(plan: EcPlan, t: (k: string) => string): string[] {
  const out: string[] = [];
  if (Number(plan.value) > 0) out.push(t('pricing.feat.methodology'));
  if (plan.featAiAssistant) out.push(t('pricing.feat.atledi'));
  if (plan.featNotifications) out.push(t('pricing.feat.notifications'));
  if (plan.featActiveLog) out.push(t('pricing.feat.activeLog'));
  if (plan.featUserManagement) out.push(t('pricing.feat.team'));
  return out;
}

export function PricingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const t = useT();
  useFavoritable('Pricing');

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.plans.all,
    queryFn: billingApi.listPlans,
  });
  const plans = data?.items ?? [];

  const checkout = useMutation({
    mutationFn: (planId: number) => billingApi.createCheckout(planId),
    // Stripe-hosted Checkout lives outside the SPA, so do a full-page nav.
    onSuccess: (session) => window.location.assign(session.url),
    onError: (e) =>
      toast({
        tone: 'error',
        title: t('pricing.checkoutFailed'),
        description: (e as Error).message,
      }),
  });

  // PRD §13.4 — DPA must be accepted before the first paid-plan checkout.
  // Queue the chosen plan, then either pass straight through (DPA hash
  // already accepted) or render the modal and resume on `onDone`.
  const [pendingPlan, setPendingPlan] = useState<EcPlan | null>(null);
  const [dpaModalOpen, setDpaModalOpen] = useState(false);

  const proceedToCheckout = (plan: EcPlan) => {
    if (!plan.stripePriceId) {
      toast({
        tone: 'info',
        title: t('pricing.talkToUs'),
        description: t('pricing.talkToUsBody'),
      });
      return;
    }
    checkout.mutate(plan.id);
  };

  const handleSelect = async (plan: EcPlan) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    // Free / Pilot / Scale-and-up — DPA is required only for paid plans
    // (value > 0). Free path skips the modal entirely.
    if (!plan.value || Number(plan.value) === 0) {
      proceedToCheckout(plan);
      return;
    }
    // Already-accepted DPA at this hash? Then proceed straight through.
    try {
      const existing = await legalApi.check(DPA.type, DPA.versionHash);
      if (existing) {
        proceedToCheckout(plan);
        return;
      }
    } catch {
      // Network blip — fail open and show the modal. Worse case is the
      // backend rejects the duplicate acceptance idempotently.
    }
    setPendingPlan(plan);
    setDpaModalOpen(true);
  };

  return (
    <div className="page">
      {isAuthenticated && (
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-8 inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
        >
          <ArrowLeft className="h-4 w-4" /> {t('pricing.back')}
        </button>
      )}

      <header className="mb-8 text-center sm:mb-10">
        <h1
          className="text-3xl font-bold sm:text-4xl"
          style={{ fontFamily: 'var(--font-display, "Space Grotesk", sans-serif)' }}
        >
          {t('pricing.heroTitle')}
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-[var(--color-fg-muted)]">
          {t('pricing.heroBody')}
        </p>
      </header>

      <div className="mx-auto grid max-w-[1100px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading && plans.length === 0
          ? null
          : plans.map((plan) => (
              <XamplinPriceCard
                key={plan.id}
                plan={plan}
                popular={plan.slug === POPULAR_SLUG}
                features={featureBullets(plan, t)}
                onSelect={() => handleSelect(plan)}
                t={t}
              />
            ))}
      </div>

      {/* PRD §13.4 — DPA clickthrough before Stripe checkout. Resumes
          `pendingPlan` on accept; cancels both states on dismiss. */}
      {dpaModalOpen && (
        <LegalAcceptanceModal
          document={DPA}
          onDone={() => {
            setDpaModalOpen(false);
            if (pendingPlan) {
              const next = pendingPlan;
              setPendingPlan(null);
              proceedToCheckout(next);
            }
          }}
          onDismiss={() => {
            setDpaModalOpen(false);
            setPendingPlan(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Xamplin tier card — server-driven from the ec_plan catalog (value, brands,
 * seats) + per-resource caps (events, agencies). Methodology is the paid
 * upgrade; Eventer+ wears the Most-Popular badge.
 */
function XamplinPriceCard({
  plan,
  popular,
  features,
  onSelect,
  t,
}: {
  plan: EcPlanWithResources;
  popular: boolean;
  features: string[];
  onSelect: () => void;
  t: (k: string, vars?: Record<string, string | number>) => string;
}) {
  const value = Number(plan.value ?? 0);
  const isFree = value === 0;
  const isEnterprise = plan.slug === 'enterprise';
  const period = plan.pricePeriod === 'month' ? t('priceCard.period.month') : t('priceCard.period.year');
  const events = capOf(plan, 'events');
  const agencies = capOf(plan, 'agencies');
  const priceText = isEnterprise ? t('pricing.fromPrice', { v: `$${value.toLocaleString()}` }) : `$${value.toLocaleString()}`;

  const caps: Array<[string, string]> = [
    [t('pricing.cap.brands'), capLabel(plan.maxBrands)],
    [t('pricing.cap.events'), capLabel(events)],
    [t('pricing.cap.seats'), capLabel(plan.maxUsers)],
    [t('pricing.cap.agencies'), capLabel(agencies)],
  ];

  return (
    <article
      className={cn(
        'relative flex flex-col rounded-2xl border p-6 transition-transform',
        popular
          ? 'border-[var(--color-xk-accent)] bg-[var(--color-accent-04)] -translate-y-1 shadow-lg'
          : 'border-[var(--color-xk-border)] bg-[var(--color-xk-surface)]',
      )}
    >
      {popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[var(--color-xk-accent)] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
          {t('pricing.mostPopular')}
        </span>
      )}

      <h3 className="mono text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
        {plan.name}
      </h3>
      <div className="mt-1.5 flex items-baseline gap-1">
        <span className="font-display text-3xl font-extrabold text-[var(--color-xk-lavender)]">
          {priceText}
        </span>
        {!isFree && <span className="text-xs text-[var(--color-xk-text-subtle)]">{period}</span>}
      </div>

      <ul className="mt-5 flex flex-col gap-2.5">
        {caps.map(([label, val]) => (
          <li key={label} className="flex items-baseline justify-between gap-2">
            <span className="mono text-[11px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
              {label}
            </span>
            <span className="font-display text-sm font-semibold text-[var(--color-xk-lavender)]">{val}</span>
          </li>
        ))}
      </ul>

      <ul className="mt-4 flex flex-1 flex-col gap-1.5 border-t border-[var(--color-xk-border)] pt-3 text-xs text-[var(--color-xk-text-muted)]">
        <li className="flex items-center gap-2">
          <span style={{ color: XAMPLIN_PURPLE }}>✓</span>
          {t('pricing.feat.operations')}
        </li>
        {features.map((f) => (
          <li key={f} className="flex items-center gap-2">
            <span style={{ color: XAMPLIN_PURPLE }}>✓</span>
            {f}
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'mt-5 rounded-lg px-4 py-2.5 text-sm font-bold transition-colors',
          popular
            ? 'bg-[var(--color-xk-accent)] text-white hover:opacity-90'
            : 'border border-[var(--color-xk-border)] text-[var(--color-xk-lavender)] hover:border-[var(--color-xk-accent)]',
        )}
      >
        {isEnterprise ? t('pricing.talkToUs') : isFree ? t('pricing.startFree') : t('pricing.choose', { tier: plan.name })} →
      </button>
    </article>
  );
}
